import { useState, useCallback, useRef } from "react";
import { useAudioRecorder } from "@/hooks/use-audio-recorder";
import type { MicPermission } from "@/hooks/use-audio-recorder";

export type InlineVoiceStatus = "idle" | "recording" | "transcribing" | "done" | "error";

export interface UseInlineVoiceInputReturn {
  isRecording: boolean;
  status: InlineVoiceStatus;
  transcript: string;
  error: string | null;
  micPermission: MicPermission;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  reset: () => void;
  isMediaRecorderSupported: boolean;
  recordingTime: number;
  formatTime: (seconds: number) => string;
}

const MAX_INLINE_DURATION_SECONDS = 60;

export function useInlineVoiceInput(options: {
  onTranscript?: (text: string) => void;
  transcribeAudio: (audioBase64: string) => Promise<{ text: string }>;
}): UseInlineVoiceInputReturn {
  const { onTranscript, transcribeAudio } = options;

  const [status, setStatus] = useState<InlineVoiceStatus>("idle");
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef(false);

  const isMediaRecorderSupported =
    typeof MediaRecorder !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia;

  const handleRecordingComplete = useCallback(
    async (blob: Blob) => {
      if (abortRef.current) return;
      setStatus("transcribing");
      setError(null);

      try {
        const reader = new FileReader();
        const base64Promise = new Promise<string>((resolve, reject) => {
          reader.onloadend = () => {
            if (typeof reader.result === "string") {
              const base64 = reader.result.split(",")[1];
              if (base64) resolve(base64);
              else reject(new Error("Failed to encode audio"));
            } else {
              reject(new Error("FileReader returned unexpected result type"));
            }
          };
          reader.onerror = () => reject(new Error("Failed to read audio file"));
        });
        reader.readAsDataURL(blob);
        const audioBase64 = await base64Promise;

        if (abortRef.current) return;
        const result = await transcribeAudio(audioBase64);
        if (abortRef.current) return;

        setTranscript(result.text);
        setStatus("done");
        onTranscript?.(result.text);
      } catch (err) {
        if (abortRef.current) return;
        const message =
          err instanceof Error ? err.message : "Transcription failed. Please try again.";
        const clean = message.replace(/^HTTP \d{3}[^:]*:\s*/, "");
        setError(clean || "Transcription failed. Please try again.");
        setStatus("error");
      }
    },
    [transcribeAudio, onTranscript]
  );

  const recorder = useAudioRecorder({
    onRecordingComplete: handleRecordingComplete,
    maxDurationSeconds: MAX_INLINE_DURATION_SECONDS,
  });

  const startRecording = useCallback(async () => {
    abortRef.current = false;
    setStatus("idle");
    setTranscript("");
    setError(null);
    await recorder.startRecording();
    if (!recorder.deviceError) {
      setStatus("recording");
    }
  }, [recorder]);

  const stopRecording = useCallback(() => {
    recorder.stopRecording();
    setStatus("transcribing");
  }, [recorder]);

  const reset = useCallback(() => {
    abortRef.current = true;
    setStatus("idle");
    setTranscript("");
    setError(null);
  }, []);

  const derivedStatus: InlineVoiceStatus = recorder.deviceError
    ? "error"
    : recorder.isRecording
    ? "recording"
    : status;

  const derivedError = recorder.deviceError ?? error;

  return {
    isRecording: recorder.isRecording,
    status: derivedStatus,
    transcript,
    error: derivedError,
    micPermission: recorder.micPermission,
    startRecording,
    stopRecording,
    reset,
    isMediaRecorderSupported,
    recordingTime: recorder.recordingTime,
    formatTime: recorder.formatTime,
  };
}
